// The 6-digit code that turns a saved account into a usable one (plan §5).
//
// The code was mailed to the configured mailbox itself, which is exactly what
// proves the credentials work end to end. Five wrong attempts burn the code;
// resending is behind a 60 s cooldown the server also enforces (429), so the
// countdown here is a courtesy that keeps the button from lying.

import { useCallback, useEffect, useState } from 'react';
import { Button, Input, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import type { EmailTransportClient } from '@/services/email-transport.service';
import type { EmailTransportConfigMasked } from '@bbpos/types';

const RESEND_COOLDOWN_SECONDS = 60;

/** Seconds still owed on the cooldown, given when the last code went out. */
export function cooldownRemaining(sentAt: string | null | undefined, now: number): number {
  if (!sentAt) return 0;
  const sent = new Date(sentAt).getTime();
  if (Number.isNaN(sent)) return 0;
  const elapsed = Math.floor((now - sent) / 1000);
  return Math.max(RESEND_COOLDOWN_SECONDS - elapsed, 0);
}

function formatStamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/**
 * A 400 from `verify` carries how many tries are left. The global exception
 * filter currently forwards `{ statusCode, error, message, details }` only, so
 * treat the field as optional and fall back to the message.
 */
function attemptsLeftFrom(err: ApiError): number | null {
  const raw = err.body?.attempts_left;
  return typeof raw === 'number' ? raw : null;
}

export function EmailVerificationCard({
  config,
  client,
  canEdit,
  expiresAt,
  onVerified,
}: {
  config: EmailTransportConfigMasked;
  client: EmailTransportClient;
  canEdit: boolean;
  /** Expiry reported by the upsert/resend response, if this session has one. */
  expiresAt?: string | null;
  onVerified: (config: EmailTransportConfigMasked) => void | Promise<void>;
}) {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [sentAt, setSentAt] = useState<string | null>(config.verification_sent_at);
  const [expiry, setExpiry] = useState<string | null>(expiresAt ?? null);
  const [seconds, setSeconds] = useState(() => cooldownRemaining(config.verification_sent_at, Date.now()));

  useEffect(() => {
    setSentAt(config.verification_sent_at);
  }, [config.verification_sent_at]);

  useEffect(() => {
    if (expiresAt) setExpiry(expiresAt);
  }, [expiresAt]);

  useEffect(() => {
    setSeconds(cooldownRemaining(sentAt, Date.now()));
    if (!sentAt) return;
    const timer = setInterval(() => {
      setSeconds(cooldownRemaining(sentAt, Date.now()));
    }, 1000);
    return () => clearInterval(timer);
  }, [sentAt]);

  const handleVerify = useCallback(async () => {
    setVerifying(true);
    try {
      const result = await client.verify(code);
      setAttemptsLeft(null);
      setCode('');
      toast({ variant: 'success', title: 'Sender verified', description: config.email });
      await onVerified(result.config);
    } catch (err) {
      if (err instanceof ApiError) {
        const left = attemptsLeftFrom(err);
        setAttemptsLeft(left);
        toast({
          variant: 'warning',
          title: err.message,
          description:
            left !== null
              ? `${left} ${left === 1 ? 'attempt' : 'attempts'} left`
              : 'Request a new code if this one has expired.',
        });
      } else {
        toast({ variant: 'error', title: 'Could not verify the code' });
      }
    } finally {
      setVerifying(false);
    }
  }, [client, code, config.email, onVerified, toast]);

  const handleResend = useCallback(async () => {
    setResending(true);
    try {
      const outcome = await client.resendVerification();
      if (outcome.sent) {
        setSentAt(new Date().toISOString());
        setExpiry(outcome.expires_at ?? null);
        setAttemptsLeft(null);
        toast({
          variant: 'success',
          title: 'New code sent',
          description: outcome.sent_to ?? config.email,
        });
      } else {
        toast({ variant: 'warning', title: 'Could not send the code', description: outcome.error });
      }
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Resend failed',
        description: err instanceof ApiError ? err.message : 'Could not reach the mail server',
      });
    } finally {
      setResending(false);
    }
  }, [client, config.email, toast]);

  const expiryLabel = formatStamp(expiry);
  const codeReady = /^\d{6}$/.test(code);

  return (
    <div
      className={cn(GLASS_FORM, 'animate-fade-in space-y-4 p-6')}
      data-testid="email-verification-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-warning-500 to-danger-500 text-sm font-bold text-white shadow-sm">
          6
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
            Enter the verification code
          </p>
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
            We sent a 6-digit code to <span className="font-semibold">{config.email}</span>. Check
            that mailbox — receiving it is what proves the account can send.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Input
            label="Code"
            inputMode="numeric"
            placeholder="123456"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
            disabled={!canEdit}
            name="code"
            inputClassName="tracking-[0.4em] text-center font-mono"
          />
        </div>
        <Button
          variant="primary"
          loading={verifying}
          disabled={!canEdit || !codeReady}
          onClick={() => void handleVerify()}
        >
          Verify
        </Button>
        <Button
          variant="ghost"
          loading={resending}
          disabled={!canEdit || seconds > 0}
          onClick={() => void handleResend()}
          data-testid="resend-code"
        >
          {seconds > 0 ? `Resend code in ${seconds}s` : 'Resend code'}
        </Button>
      </div>

      {attemptsLeft !== null && (
        <p
          className="text-xs font-medium text-danger-600 dark:text-danger-400"
          data-testid="attempts-left"
        >
          {attemptsLeft === 0
            ? 'No attempts left — request a new code.'
            : `${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left before this code is cancelled.`}
        </p>
      )}

      {expiryLabel && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          This code expires at {expiryLabel}.
        </p>
      )}
    </div>
  );
}
